import { useEffect, useState } from "react";
import { ClipboardCheck, Plus, Sparkles, Loader2, FileText, Download, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import MicButton from "@/components/ui/MicButton";
import SafeBlock from "@/components/ui/SafeBlock";
import ReportShell from "@/components/reports/ReportShell";
import { startPeriodeObsJob, fetchPeriodeObsJob, fetchPeriodeObsReports, deletePeriodeObsReport, downloadPeriodeObsPdf } from "@/lib/periodeObs";

const KIND = "periode_observation";
const fmt = (n) => (n === null || n === undefined || n === "" ? null : Number(n).toLocaleString("fr-FR"));

function TresoBadges({ cible, reelle }) {
    if (cible === null || cible === undefined || reelle === null || reelle === undefined) return null;
    const ok = Number(reelle) >= Number(cible);
    return (
        <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">Cible : {fmt(cible)} €</span>
            <span className={`rounded-full px-2.5 py-1 font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{ok ? <TrendingUp className="inline h-3.5 w-3.5 mr-1" /> : <TrendingDown className="inline h-3.5 w-3.5 mr-1" />}Réelle : {fmt(reelle)} €</span>
        </div>
    );
}

function ReportCard({ dossierId, report, onDeleted }) {
    const [content, setContent] = useState(report.content || "");
    const [busy, setBusy] = useState(false);
    async function pdf() {
        setBusy(true);
        try { await downloadPeriodeObsPdf(dossierId, report.session_id); toast.success("PDF téléchargé"); }
        catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
        finally { setBusy(false); }
    }
    async function remove() {
        if (!window.confirm("Supprimer ce rapport d'étape ?")) return;
        setBusy(true);
        try { await deletePeriodeObsReport(dossierId, report.session_id); onDeleted(report.session_id); toast.success("Rapport supprimé"); }
        catch (e) { toast.error(e.message); setBusy(false); }
    }
    return (
        <Card data-testid={`po-report-${report.session_id}`} className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <FileText className="h-4 w-4 text-primary" />
                <h4 className="font-display text-base text-foreground">{report.titre || "Rapport d'étape"}</h4>
                {report.date_audience && <span className="text-xs text-muted-foreground">Audience : {report.date_audience}</span>}
                <span className="ml-auto text-xs text-muted-foreground">{report.created_at ? new Date(report.created_at).toLocaleString("fr-FR") : ""}</span>
            </div>
            <TresoBadges cible={report.treso_cible} reelle={report.treso_reelle} />
            <SafeBlock label="Le rapport"><ReportShell dossierId={dossierId} sessionId={report.session_id} kind={KIND} content={content} onContentChange={setContent} charts={[]} onChartsChange={() => {}} streaming={false} /></SafeBlock>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
                <Button variant="ghost" size="sm" data-testid="po-delete" onClick={remove} disabled={busy} className="rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /> Supprimer</Button>
                <Button variant="outline" data-testid="po-export-pdf" onClick={pdf} disabled={busy} className="rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger PDF</Button>
            </div>
        </CardContent></Card>
    );
}

export default function PeriodeObservationSection({ dossierId }) {
    const [reports, setReports] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ titre: "Rapport d'étape", date_audience: "", treso_cible: "", treso_reelle: "", briefing: "" });
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");

    useEffect(() => {
        fetchPeriodeObsReports(dossierId).then((r) => setReports(r.rapports)).catch(() => setReports([]));
    }, [dossierId]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function generate() {
        if (generating) return;
        setGenerating(true); setStage("preparation");
        try {
            const payload = {
                titre: form.titre.trim() || "Rapport d'étape", date_audience: form.date_audience || null,
                treso_cible: form.treso_cible === "" ? null : Number(form.treso_cible),
                treso_reelle: form.treso_reelle === "" ? null : Number(form.treso_reelle), briefing: form.briefing || null,
            };
            const { job_id } = await startPeriodeObsJob(dossierId, payload);
            for (;;) {
                await new Promise((r) => setTimeout(r, 3000));
                const j = await fetchPeriodeObsJob(dossierId, job_id);
                setStage(j.stage);
                if (j.stage === "done") {
                    setReports((rs) => [{ session_id: j.session_id, created_at: new Date().toISOString(), content: j.content || "", ...payload }, ...(rs || [])]);
                    setShowForm(false);
                    setForm({ titre: "Rapport d'étape", date_audience: "", treso_cible: "", treso_reelle: "", briefing: "" });
                    toast.success("Rapport d'étape généré");
                    break;
                }
                if (j.stage === "error") { toast.error("Erreur IA", { description: String(j.error), duration: 12000 }); break; }
            }
        } catch (e) { toast.error("Génération impossible", { description: e.message }); }
        finally { setGenerating(false); setStage(""); }
    }

    return (
        <section data-testid="periode-observation-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><ClipboardCheck className="h-4 w-4 text-primary-foreground" strokeWidth={1.75} /></div>
                    <div>
                        <h3 className="font-display text-xl text-foreground">3 - Audiences de Période d&apos;Observation</h3>
                        <p className="text-xs text-muted-foreground">Rapports d&apos;avancement pour le Juge-Commissaire et l&apos;AJ · suivi « Réalisé vs Prévisionnel » de la trésorerie.</p>
                    </div>
                </div>
                <Button data-testid="po-new" onClick={() => setShowForm((v) => !v)} disabled={generating} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Nouveau rapport d&apos;étape</Button>
            </div>

            {showForm && (
                <Card data-testid="po-form" className="border-indigo-200 bg-indigo-50/40 shadow-sm"><CardContent className="pt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><label className="text-sm text-muted-foreground">Titre du rapport</label><Input data-testid="po-titre" value={form.titre} onChange={set("titre")} disabled={generating} className="h-10 bg-background" /></div>
                        <div className="space-y-1.5"><label className="text-sm text-muted-foreground">Date de l&apos;audience de point d&apos;étape</label><Input type="date" data-testid="po-date" value={form.date_audience} onChange={set("date_audience")} disabled={generating} className="h-10 bg-background" /></div>
                        <div className="space-y-1.5"><label className="text-sm text-muted-foreground">Trésorerie Prévisionnelle (Cible €)</label><Input type="number" data-testid="po-treso-cible" value={form.treso_cible} onChange={set("treso_cible")} disabled={generating} placeholder="ex: 50000" className="h-10 bg-background" /></div>
                        <div className="space-y-1.5"><label className="text-sm text-muted-foreground">Trésorerie Réelle Constatée (€)</label><Input type="number" data-testid="po-treso-reelle" value={form.treso_reelle} onChange={set("treso_reelle")} disabled={generating} placeholder="ex: 42000" className="h-10 bg-background" /></div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm text-muted-foreground">Briefing (mesures de restructuration engagées)</label>
                        <div className="flex items-start gap-2">
                            <Textarea data-testid="po-briefing" value={form.briefing} onChange={set("briefing")} disabled={generating} placeholder="Réductions de coûts, licenciements, fermetures de sites, renégociations…" className="bg-background resize-y text-sm min-h-[240px]" />
                            <MicButton testId="po-briefing-mic" onText={(t) => setForm((f) => ({ ...f, briefing: (f.briefing ? f.briefing + " " : "") + t }))} />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button data-testid="po-generate" onClick={generate} disabled={generating} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">{generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? (stage === "generation" ? "Rédaction…" : "Lecture du dossier…") : "Générer le rapport d'étape"}</Button>
                    </div>
                </CardContent></Card>
            )}

            {reports === null ? (
                <p className="text-sm text-muted-foreground">Chargement…</p>
            ) : reports.length === 0 && !showForm ? (
                <div data-testid="po-empty" className="rounded-md border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">Aucun rapport d&apos;étape. Cliquez sur « Nouveau rapport d&apos;étape » pour en générer un.</div>
            ) : (
                <div className="space-y-6">{(reports || []).map((r) => <ReportCard key={r.session_id} dossierId={dossierId} report={r} onDeleted={(sid) => setReports((rs) => rs.filter((x) => x.session_id !== sid))} />)}</div>
            )}
        </section>
    );
}
